import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, Table, Alert, Spinner } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaDownload, FaFileExcel, FaUserTie, FaFilter } from "react-icons/fa";
import { trabajadorApi } from "../../../services/api/trabajadorApi";
import pythonApiClient from "../../../services/api/pythonBaseApi";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";

const ReporteTrabajadores = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const selectedProject = JSON.parse(localStorage.getItem("selectedProject") || "null");

  const [loading, setLoading] = useState(false);
  const [trabajadores, setTrabajadores] = useState([]);
  const [filtros, setFiltros] = useState({
    proyecto_id: selectedProject?.id || null,
    cliente_id: null,
    empresa_colaboradora_id: null,
    activo: true,
  });
  const [clientes, setClientes] = useState([]);
  const [empresasColaboradoras, setEmpresasColaboradoras] = useState([]);

  useEffect(() => {
    if (selectedProject?.id) {
      setFiltros(prev => ({ ...prev, proyecto_id: selectedProject.id }));
    }
  }, [selectedProject]);

  useEffect(() => {
    if (filtros.cliente_id) {
      loadEmpresasColaboradoras(filtros.cliente_id);
    } else {
      setEmpresasColaboradoras([]);
    }
  }, [filtros.cliente_id]);

  const loadClientes = async () => {
    try {
      const response = await pythonApiClient.get("/mantenimientos/clientes", {
        params: { activo: true },
      });
      setClientes(response.data || []);
    } catch (error) {
      console.error("Error al cargar clientes:", error);
    }
  };

  const loadEmpresasColaboradoras = async (clienteId) => {
    try {
      const response = await pythonApiClient.get(`/mantenimientos/clientes/${clienteId}/empresas-colaboradoras`);
      setEmpresasColaboradoras(response.data || []);
    } catch (error) {
      console.error("Error al cargar empresas colaboradoras:", error);
      setEmpresasColaboradoras([]);
    }
  };

  useEffect(() => {
    loadClientes();
  }, []);

  const handleGenerarReporte = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const params = {
        activo: filtros.activo,
      };
      
      if (filtros.proyecto_id) {
        params.proyecto_id = filtros.proyecto_id;
      }
      if (filtros.cliente_id) {
        params.cliente_id = filtros.cliente_id;
      }
      if (filtros.empresa_colaboradora_id) {
        params.empresa_colaboradora_id = filtros.empresa_colaboradora_id;
      }

      const response = await pythonApiClient.get("/procesos/hoteleria/carga-masiva/trabajadores/reporte", {
        params,
        headers: {
          "X-Proyecto-ID": filtros.proyecto_id,
        },
      });

      if (response.data.success) {
        setTrabajadores(response.data.trabajadores || []);
        toast.success(`Reporte generado: ${response.data.total} trabajadores encontrados`);
      } else {
        toast.error(response.data.message || "Error al generar el reporte");
        setTrabajadores([]);
      }
    } catch (error) {
      console.error("Error al generar reporte:", error);
      toast.error(error.response?.data?.detail || "Error al generar el reporte");
      setTrabajadores([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportarExcel = () => {
    if (trabajadores.length === 0) {
      toast.warning("No hay datos para exportar");
      return;
    }

    try {
      // Preparar datos para Excel
      const datosExcel = trabajadores.map((t) => ({
        "ID": t.id,
        "ID Trabajador Externo": t.id_trabajador_externo || "",
        "DNI": t.numero_documento_identidad,
        "Nombre Completo": t.nombre_completo,
        "Tipo Documento": t.tipo_documento_identidad || "",
        "Género": t.genero || "",
        "Cargo": t.cargo || "",
        "Área": t.area || "",
        "Categoría Cargo": t.tipo_categoria_cargo || "",
        "Régimen": t.regimen || "",
        "Turno Laboral": t.turno_laboral || "",
        "Número Contrato": t.numero_contrato || "",
        "Tipo Contrato": t.tipo_contrato || "",
        "Tipo Mano de Obra": t.tipo_mano_obra || "",
        "Centro Costo": t.centro_costo || "",
        "Departamento": t.departamento || "",
        "Teléfono": t.telefono || "",
        "Localía": t.localia || "",
        "Origen": t.tipo_origen_trabajador || "",
        "Fecha Ingreso": t.fecha_ingreso_proyecto || "",
        "Estado Trabajador": t.estado_trabajador || "",
        "Estado In Site": t.estado_trabajador_insite || "",
        "Empresa Colaboradora": t.empresa_colaboradora || "",
        "Proyecto": t.proyecto_nombre || "",
        "Cliente": t.cliente_nombre || "",
        "Activo": t.activo ? "Sí" : "No",
        "Fecha Creación": t.created_at || "",
        "Fecha Actualización": t.updated_at || "",
      }));

      // Crear workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(datosExcel);

      // Ajustar ancho de columnas
      const colWidths = [
        { wch: 8 },   // ID
        { wch: 20 },  // ID Trabajador Externo
        { wch: 12 },  // DNI
        { wch: 30 },  // Nombre Completo
        { wch: 15 },  // Tipo Documento
        { wch: 12 },  // Género
        { wch: 30 },  // Cargo
        { wch: 20 },  // Área
        { wch: 20 },  // Categoría Cargo
        { wch: 12 },  // Régimen
        { wch: 15 },  // Turno Laboral
        { wch: 15 },  // Número Contrato
        { wch: 15 },  // Tipo Contrato
        { wch: 20 },  // Tipo Mano de Obra
        { wch: 15 },  // Centro Costo
        { wch: 15 },  // Departamento
        { wch: 20 },  // Teléfono
        { wch: 15 },  // Localía
        { wch: 15 },  // Origen
        { wch: 15 },  // Fecha Ingreso
        { wch: 20 },  // Estado Trabajador
        { wch: 20 },  // Estado In Site
        { wch: 25 },  // Empresa Colaboradora
        { wch: 25 },  // Proyecto
        { wch: 25 },  // Cliente
        { wch: 8 },   // Activo
        { wch: 20 },  // Fecha Creación
        { wch: 20 },  // Fecha Actualización
      ];
      ws["!cols"] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, "Trabajadores");

      // Descargar archivo
      const fecha = new Date().toISOString().split("T")[0];
      XLSX.writeFile(wb, `reporte_trabajadores_${fecha}.xlsx`);

      toast.success("Reporte exportado exitosamente");
    } catch (error) {
      console.error("Error al exportar Excel:", error);
      toast.error("Error al exportar el reporte a Excel");
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFiltros((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value === "" ? null : parseInt(value) || value,
    }));
  };

  return (
    <>
      {/* Digital Harmony Styles para Reportes */}
      <style>
        {`
          .dh-reporte-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
            padding: 2rem 0;
          }
          
          .dh-card {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
            margin-bottom: 2rem;
          }
          
          .dh-card-header {
            border-radius: 20px 20px 0 0;
            padding: 1.25rem 1.5rem;
            border: none;
            font-weight: 700;
          }
          
          .dh-card-header-primary {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 10px 20px;
            font-weight: 600;
            font-size: 0.9rem;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-primary:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 10px 20px;
            font-weight: 600;
            font-size: 0.9rem;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #4b5563 0%, #374151 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          
          .btn-dh-success {
            background: linear-gradient(135deg, #10b981 0%, #059669 100%);
            border: none;
            color: white;
            border-radius: 10px;
            padding: 10px 20px;
            font-weight: 600;
            font-size: 0.9rem;
            box-shadow: 0 4px 14px rgba(16, 185, 129, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-success:hover {
            background: linear-gradient(135deg, #059669 0%, #047857 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(16, 185, 129, 0.4);
            color: white;
          }
          
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 0.95rem;
            transition: all 0.3s ease;
            background: rgba(255, 255, 255, 0.9);
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            background: white;
            outline: none;
          }
          
          .dh-form-control:disabled {
            background: #f3f4f6;
            cursor: not-allowed;
          }
          
          .dh-form-label {
            color: #374151;
            font-weight: 600;
            font-size: 0.95rem;
            margin-bottom: 8px;
          }
          
          .dh-alert {
            border: none;
            border-radius: 12px;
            padding: 1rem 1.25rem;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          }
          
          .dh-alert-info {
            background: linear-gradient(135deg, rgba(0, 198, 255, 0.1) 0%, rgba(0, 114, 255, 0.1) 100%);
            border-left: 4px solid #00c6ff;
            color: #0056d1;
          }
          
          .dh-table-wrapper {
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          }
          
          .dh-table {
            margin-bottom: 0;
          }
          
          .dh-table thead {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
          }
          
          .dh-table thead th {
            border: none;
            padding: 12px 16px;
            font-weight: 600;
            font-size: 0.875rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          
          .dh-table tbody tr {
            transition: all 0.2s ease;
          }
          
          .dh-table tbody tr:hover {
            background-color: #f8f9fa;
            transform: scale(1.01);
          }
          
          .dh-table tbody td {
            padding: 12px 16px;
            border-color: #e5e7eb;
            font-size: 0.9rem;
            vertical-align: middle;
          }
          
          .dh-sticky-header {
            position: sticky;
            top: 0;
            z-index: 10;
          }
        `}
      </style>

      <div className="dh-reporte-bg">
        <ToastContainer />
        <Container fluid>
          <Row>
            <Col>
              <Card className="dh-card">
                <Card.Header className="dh-card-header dh-card-header-primary">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <FaUserTie className="me-3" size={20} />
                      <h5 className="mb-0" style={{ fontSize: '1.1rem', fontWeight: 700 }}>
                        Reporte de Trabajadores
                      </h5>
                    </div>
                    <Button
                      className="btn-dh-secondary"
                      onClick={() => navigate("/reportes")}
                    >
                      <FaArrowLeft className="me-2" />
                      Volver
                    </Button>
                  </div>
                </Card.Header>
                <Card.Body className="p-4">
                  <Form onSubmit={handleGenerarReporte}>
                    <Row className="mb-4">
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="dh-form-label">Proyecto</Form.Label>
                          <Form.Control
                            type="text"
                            value={selectedProject?.nombre || "No seleccionado"}
                            disabled
                            className="dh-form-control bg-light"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="dh-form-label">Cliente</Form.Label>
                          <Form.Select
                            name="cliente_id"
                            value={filtros.cliente_id || ""}
                            onChange={handleInputChange}
                            className="dh-form-control"
                          >
                            <option value="">Todos</option>
                            {clientes.map((cliente) => (
                              <option key={cliente.id} value={cliente.id}>
                                {cliente.nombre}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="dh-form-label">Empresa Colaboradora</Form.Label>
                          <Form.Select
                            name="empresa_colaboradora_id"
                            value={filtros.empresa_colaboradora_id || ""}
                            onChange={handleInputChange}
                            disabled={!filtros.cliente_id}
                            className="dh-form-control"
                          >
                            <option value="">Todas</option>
                            {empresasColaboradoras.map((empresa) => (
                              <option key={empresa.id} value={empresa.id}>
                                {empresa.nombre}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                      <Col md={3}>
                        <Form.Group className="mb-3">
                          <Form.Label className="dh-form-label">Estado</Form.Label>
                          <Form.Select
                            name="activo"
                            value={filtros.activo === null ? "" : filtros.activo ? "true" : "false"}
                            onChange={(e) =>
                              setFiltros((prev) => ({
                                ...prev,
                                activo: e.target.value === "" ? null : e.target.value === "true",
                              }))
                            }
                            className="dh-form-control"
                          >
                            <option value="true">Activos</option>
                            <option value="false">Inactivos</option>
                            <option value="">Todos</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <div className="d-flex justify-content-end gap-3">
                          <Button
                            type="button"
                            className="btn-dh-secondary"
                            onClick={() => navigate("/reportes")}
                          >
                            Cancelar
                          </Button>
                          <Button
                            type="submit"
                            className="btn-dh-primary d-flex align-items-center"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <Spinner
                                  as="span"
                                  animation="border"
                                  size="sm"
                                  role="status"
                                  aria-hidden="true"
                                  className="me-2"
                                />
                                Generando...
                              </>
                            ) : (
                              <>
                                <FaFilter className="me-2" />
                                Generar Reporte
                              </>
                            )}
                          </Button>
                          {trabajadores.length > 0 && (
                            <Button
                              variant="success"
                              onClick={handleExportarExcel}
                              className="btn-dh-success d-flex align-items-center"
                            >
                              <FaFileExcel className="me-2" />
                              Exportar a Excel
                            </Button>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </Form>

                  {trabajadores.length > 0 && (
                    <div className="mt-4">
                      <Alert variant="info" className="dh-alert dh-alert-info">
                        <strong>{trabajadores.length}</strong> trabajadores encontrados
                      </Alert>
                      <div className="dh-table-wrapper">
                        <div style={{ maxHeight: "600px", overflowY: "auto" }}>
                          <Table striped bordered hover className="dh-table">
                            <thead className="dh-sticky-header">
                              <tr>
                                <th>ID</th>
                                <th>DNI</th>
                                <th>Nombre Completo</th>
                                <th>Cargo</th>
                                <th>Área</th>
                                <th>Categoría</th>
                                <th>Estado</th>
                                <th>Estado In Site</th>
                                <th>Teléfono</th>
                                <th>Fecha Ingreso</th>
                              </tr>
                            </thead>
                            <tbody>
                              {trabajadores.map((trabajador) => (
                                <tr key={trabajador.id}>
                                  <td>{trabajador.id}</td>
                                  <td>{trabajador.numero_documento_identidad}</td>
                                  <td>{trabajador.nombre_completo}</td>
                                  <td>{trabajador.cargo || "-"}</td>
                                  <td>{trabajador.area || "-"}</td>
                                  <td>{trabajador.tipo_categoria_cargo || "-"}</td>
                                  <td>{trabajador.estado_trabajador || "-"}</td>
                                  <td>{trabajador.estado_trabajador_insite || "-"}</td>
                                  <td>{trabajador.telefono || "-"}</td>
                                  <td>{trabajador.fecha_ingreso_proyecto || "-"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </div>
                  )}

                  {trabajadores.length === 0 && !loading && (
                    <Alert variant="info" className="dh-alert dh-alert-info mt-4">
                      No hay trabajadores para mostrar. Use los filtros y haga clic en "Generar Reporte".
                    </Alert>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default ReporteTrabajadores;
